/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

import { useParams } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';

import {
    Button,
    FlexRow,
    FlexCol,
    Placeholder,
    PlaceholderText,
    Alert,
    Stack,
    Text,
    Heading,
    RadioPillGroup,
    VisualOnlyLabel,
    STATUS,
    DisplayHtmlContent,
    TextBlock,
    AccessibleKeyboardWrapper,
    SecurityHelper,
    Icon,
    HtmlParser,
    useFlags,
    FauxTextField,
} from 'sarsaparilla';

import { MustLogInNotice } from 'shared-ui/components/MustLogInNotice/MustLogInNotice';
import { fetchTourAvailability } from '../../actions/tourAvailabilityActions.js';
import { TourCalendarContainer } from '../../containers/TourDetailsPage/TourCalendarContainer';
import AvailabilityAlerts from '../common/AvailabilityAlerts';
import GuestCounter from '../common/GuestCounter';
import { GuestCheckoutNotice } from './GuestCheckoutNotice';

import {
    getMaxTotalGuests,
    getInitialCounts,
    getTimeOptions,
} from '../../utils/generalTicketUtils';

import {
    getGuestCounterOptions,
    getMinTotalGuestsByGuestType,
    getMaxTotalGuestsByGuestType,
    getCustomGuestTypeText,
} from '../../utils/guestTypesUtils';
import { getSingularOrPluralText } from '../../utils';

export function BookingForm({
    isBookingInProgress,
    onBuyClick,
    bookingError,
    counts,
    setCounts,
    time,
    setTime,
    isDateBlocked,
    openCalendar = false,
    showLoginNotice,
    showWalkupNotice = false,
    showNoTicketRequiredNotice = false,
    facilityId,
    guestCheckoutEnabled,
    onFocus = () => {},
}) {
    const dispatch = useDispatch();
    const { tourId } = useParams();
    const isMobile = SecurityHelper.isMobileApp();
    const { testRateLimiting } = useFlags();

    const accountType = useSelector((state) => state.timedEntry.accountType.accountType);
    const facilityData = useSelector((state) => state.timedEntry.facility);
    const dateM = useSelector((state) => state.timedEntry.filters?.date);
    const guestCountFromFilters = useSelector(
        (state) => state.timedEntry.filters?.guestCount
    );

    const { loadingStatus } = useSelector((state) => state.timedEntry.tourAvailability);
    const isTourAvailabilityLoading = loadingStatus === 'PENDING';

    const tourTimesData = useSelector((state) => state.timedEntry.tourTimes);
    const allGuestTypes =
        useSelector((state) => state.timedEntry.facility?.guest_types) || [];
    const tourMap = useSelector((state) => state.timedEntry.tours?.tourMap);

    const tourTimes = useMemo(
        () => tourTimesData?.data?.[tourId] || [],
        [tourId, tourTimesData?.data]
    );
    const isTourTimesLoading = tourTimesData.loadingStatus === STATUS.PENDING;
    const tourData = tourMap?.[tourId] || {};

    const {
        allowed_guest_type_ids: allowedGuestTypeIds,
        purchase_minimums_by_guest_type: purchaseMinimumsByGuestType,
        purchase_maximums_by_guest_type: purchaseMaximumsByGuestType,
        accepts_passes: acceptsPasses,
        passes_by_guest_type_id: passesByGuestTypeId,
        calculate_booking_window_close: bookThroughoutTourDuration,
    } = tourData;

    const customGuestAlertText = getCustomGuestTypeText(facilityData?.facility_id);

    const guestTypes = getGuestCounterOptions({
        guestTypes: allGuestTypes,
        allowedGuestTypeIds,
    });

    const minTotalGuestsByGuestType = getMinTotalGuestsByGuestType(
        purchaseMinimumsByGuestType
    );
    const maxTotalGuestsByGuestType = getMaxTotalGuestsByGuestType(
        purchaseMaximumsByGuestType
    );

    const timezone = facilityData?.facility_timezone;
    const maxTotalGuests = getMaxTotalGuests({
        facility: facilityData,
        tour: tourData,
        accountType,
    });

    const maxTotalTitle = getSingularOrPluralText(maxTotalGuests, 'ticket', 'tickets');

    /**
     * Set up initial state of the guest counter.
     */
    useEffect(() => {
        const initialCounts = getInitialCounts(
            allowedGuestTypeIds,
            guestCountFromFilters,
            maxTotalGuests,
            minTotalGuestsByGuestType
        );

        setCounts(initialCounts);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allowedGuestTypeIds, guestCountFromFilters, maxTotalGuests, setCounts]);

    let countTotal = 0;
    for (const key in counts) {
        if (typeof counts[key] === 'number') {
            countTotal += counts[key];
        }
    }

    /**
     * If the user selects a time and then changes the quantity to a value that exceeds
     * the availability, we deselect the time
     */
    useEffect(() => {
        if (time && countTotal) {
            const [selectedTour] = tourTimes.filter((item) => item.time === time);
            if (selectedTour?.availableCount < countTotal) {
                setTime('');
            }
        }
    }, [countTotal, time, setTime, tourTimes]);

    const calculateBookingWindowClose = tourData?.calculate_booking_window_close;

    // Create the RadioPillGroup options
    const {
        timeOptions: timeOptionsRaw,
        firstReleasedDate,
        secondReleasedDate,
        moreAvailableDate,
        additionalMoreAvailableDate,
        hasAvailability,
    } = getTimeOptions({
        tourTimes,
        countTotal,
        timezone,
        calculateBookingWindowClose,
    });

    const getFormattedHtmlHelper = (
        displayDuration,
        availableCount,
        availableWalkupCount,
        unreleasedCount,
        hideAvailableCount,
        onlyHasWalkupAvailability
    ) => {
        let availableText = `(${availableCount || unreleasedCount || availableWalkupCount} left)`;
        let firstComeFirstServedClass = '';
        if (onlyHasWalkupAvailability) {
            availableText = `<span class="sarsa-text weight-bold">FF</span>&nbsp;only ${availableText}`;
            firstComeFirstServedClass = ' te-radio-pill--first-come-first-served';
        }
        const availableHtml = !hideAvailableCount
            ? `<div class="te-radio-pill-available">${availableText}</div>`
            : '';
        const labelText = `
            <div class="te-radio-pill${firstComeFirstServedClass}" aria-hidden="true">
                <div class="te-radio-pill-time">${displayDuration}</div>
                ${availableHtml}
            </div>
        `;
        return <DisplayHtmlContent html={labelText} />;
    };

    const startText = bookThroughoutTourDuration ? 'Entry Window' : 'Starting Time';
    const timeOptions = timeOptionsRaw
        ?.map((item) => {
            const onlyHasWalkupAvailability =
                !item?.availableCount &&
                !item?.unreleasedCount &&
                !!item?.availableWalkupCount;

            const explanationText = onlyHasWalkupAvailability
                ? ' (This tickets are First-come, first-served and may be available once you are physically on site at this location, but there is no guarantee.)'
                : '';

            return {
                ...item,
                label: getFormattedHtmlHelper(
                    item?.displayDuration,
                    item?.availableCount,
                    item?.availableWalkupCount,
                    item?.unreleasedCount,
                    item?.hideAvailableCount,
                    onlyHasWalkupAvailability
                ),
                ariaLabel: `${startText} ${item?.displayDuration} with ${item?.availableCount || item?.unreleasedCount || item?.availableWalkupCount} left${explanationText}`,
            };
        })
        .filter(
            (timeOption) =>
                timeOption?.availableCount > 0 ||
                timeOption?.unreleasedCount > 0 ||
                timeOption?.availableWalkupCount > 0
        );

    /**
     * If there is only one time option, pre-select it and enable the 'Add to Cart' button
     * If there are no available time options, deselect the time
     */
    useEffect(() => {
        if (!hasAvailability) {
            setTime('');
        } else if (timeOptions.length === 1) {
            setTime(timeOptions[0].value);
        }
    }, [timeOptions, hasAvailability, setTime]);

    const showRadioPills = timeOptions?.length > 0 || isTourTimesLoading;
    const showSelectDateMessage =
        !timeOptions?.length && tourTimesData.loadingStatus === STATUS.IDLE;
    const showErrorMessage = tourTimesData.loadingStatus === STATUS.ERROR;

    const keybinds = {
        r: {
            handler: () => {
                document.getElementById('request-tickets').focus();
            },
            instruction: "Focus the 'Request Tickets' button",
            icon: <kbd>r</kbd>,
        },
    };

    let nextReleaseDateText = '';
    if (moreAvailableDate) {
        nextReleaseDateText = `${moreAvailableDate.format(
            'MMMM D, YYYY'
        )} at ${moreAvailableDate.format('hh:mm a z')}`;
    }

    function handleRefreshAvailability() {
        dispatch(
            fetchTourAvailability({
                facilityId,
                tourId,
                date: dateM || moment(),
                forceRefresh: true,
            })
        );
    }

    const formatDate = (date) => {
        if (date) {
            return `${date?.format('MMMM D, YYYY')} at ${date?.format('hh:mm a z')}`;
        }

        return '';
    };

    const renderWalkupNotice = () => {
        if (!isTourTimesLoading && showWalkupNotice) {
            return (
                <div className="te-date-time-guest-picker__notice-container">
                    <Heading className="mb-half" headingLevel={2} appearance="h6">
                        First Come, First Served Available at this location:
                    </Heading>
                    <ul className="te-date-time-guest-picker__list">
                        <li>
                            <Text size="sm">
                                First-come, first-served tickets may be available once you
                                are physically on site at this location, but there is no
                                guarantee.
                            </Text>
                        </li>
                    </ul>
                </div>
            );
        }

        return null;
    };

    const renderNoTicketRequiredNotice = () => {
        if (showNoTicketRequiredNotice) {
            return (
                <div className="te-date-time-guest-picker__notice-container">
                    <Text size="sm">
                        {`A ticket is not required to visit this location on ${dateM.format('MMMM D, YYYY')}`}
                    </Text>
                </div>
            );
        }

        return null;
    };

    const [selectedItemByTime] = tourTimes.filter((item) => item.time === time);

    const renderAllTicketReleased = () => {
        return (
            <>
                {firstReleasedDate && (
                    <li>
                        <Text size="sm">
                            {`The first release for tickets was on ${formatDate(firstReleasedDate)}.`}
                        </Text>
                    </li>
                )}

                {secondReleasedDate && selectedItemByTime?.hasSecondary && (
                    <li>
                        <Text size="sm">
                            {`The second release for tickets was on ${formatDate(secondReleasedDate)}.`}
                        </Text>
                    </li>
                )}
            </>
        );
    };

    const renderInfoNotice = () => {
        const showReleaseDateInfo =
            firstReleasedDate ||
            secondReleasedDate ||
            nextReleaseDateText ||
            additionalMoreAvailableDate;
        const allTicketsReleased =
            !nextReleaseDateText &&
            !additionalMoreAvailableDate &&
            (firstReleasedDate || secondReleasedDate);
        const headerText = allTicketsReleased
            ? 'All Tickets for this date have been released:'
            : showReleaseDateInfo
              ? 'Release information for this date:'
              : '';

        if (
            !isTourTimesLoading &&
            (showReleaseDateInfo || showWalkupNotice || showNoTicketRequiredNotice)
        ) {
            return (
                <Alert type="info" heading={headerText}>
                    {showReleaseDateInfo && (
                        <ul className="te-date-time-guest-picker__list">
                            {allTicketsReleased && renderAllTicketReleased()}
                            {!allTicketsReleased && firstReleasedDate && (
                                <li>
                                    <Text size="sm">
                                        {`Tickets were released on ${formatDate(firstReleasedDate)}.`}
                                    </Text>
                                </li>
                            )}
                            {nextReleaseDateText && (
                                <li>
                                    <Text size="sm">
                                        {`Tickets will be released on ${nextReleaseDateText}.`}
                                    </Text>
                                </li>
                            )}
                            {additionalMoreAvailableDate && (
                                <li>
                                    <Text size="sm">
                                        {`Additional tickets will be made available on ${formatDate(additionalMoreAvailableDate)}.`}
                                    </Text>
                                </li>
                            )}
                        </ul>
                    )}
                    {renderWalkupNotice()}
                    {renderNoTicketRequiredNotice()}
                </Alert>
            );
        }

        return null;
    };

    return (
        <Stack space="lg" className="te-date-time-guest-picker">
            <Stack space="lg">
                <FlexRow className="te-date-time-guests-picker">
                    <FlexCol md={6}>
                        <Stack space="xxs">
                            <TourCalendarContainer
                                openCalendar={openCalendar}
                                onFocus={onFocus}
                            />
                        </Stack>
                    </FlexCol>
                    <FlexCol md={6}>
                        {maxTotalGuests === 1 && guestTypes.length === 1 ? (
                            <>
                                <FauxTextField label="Quantity" value="1 Ticket" />
                                <Alert type="info" className="mt-1">
                                    There is a max of 1 ticket allowed
                                </Alert>
                            </>
                        ) : (
                            <Stack space="xxs">
                                <VisualOnlyLabel>Quantity</VisualOnlyLabel>
                                <GuestCounter
                                    placeholder="Add Quantity ..."
                                    guestTypes={guestTypes}
                                    title="Tickets"
                                    minTotalTitle="ticket"
                                    maxTotalTitle={maxTotalTitle}
                                    onCountChange={setCounts}
                                    value={counts}
                                    maxTotalGuests={maxTotalGuests}
                                    minimumCountByGuestType={minTotalGuestsByGuestType}
                                    maximumCountByGuestType={maxTotalGuestsByGuestType}
                                    acceptsPasses={acceptsPasses}
                                    passesByGuestTypeId={passesByGuestTypeId}
                                    ticketCountByGuestTypeId={counts}
                                    customGuestAlertText={customGuestAlertText}
                                    facilityId={facilityData?.facility_id}
                                />
                            </Stack>
                        )}
                    </FlexCol>
                </FlexRow>

                {showSelectDateMessage && (
                    <Stack space="xxs">
                        <Text fontWeight="semibold">
                            {bookThroughoutTourDuration
                                ? 'Entry Window'
                                : 'Starting Time'}
                        </Text>
                        <Text color="gray">
                            Select a date above to see a list of times ...
                        </Text>
                    </Stack>
                )}

                {showRadioPills && (
                    <AccessibleKeyboardWrapper keybinds={keybinds} isMobile={isMobile}>
                        <RadioPillGroup
                            isLoading={isTourTimesLoading}
                            description={
                                bookThroughoutTourDuration
                                    ? 'Entry Window'
                                    : 'Starting Time'
                            }
                            options={timeOptions}
                            value={time}
                            onChange={setTime}
                        />
                    </AccessibleKeyboardWrapper>
                )}

                {renderInfoNotice()}

                {showErrorMessage && (
                    <Alert type="error">
                        Sorry, an error occurred while trying to fetch available times.
                        Refresh your browser and try again.
                        {isTourAvailabilityLoading ? (
                            <PlaceholderText lines={1} width="30%" />
                        ) : (
                            <Button
                                appearance="link"
                                iconBeforeElement={<Icon iconName="refresh" />}
                                onClick={handleRefreshAvailability}
                            >
                                Refresh Availability
                            </Button>
                        )}
                    </Alert>
                )}
            </Stack>

            <Stack>
                <Stack space="xs">
                    {showLoginNotice && <MustLogInNotice />}
                    {guestCheckoutEnabled && <GuestCheckoutNotice />}
                    {!!bookingError && (
                        <Alert type="error">
                            <TextBlock>
                                <div>
                                    <HtmlParser html={bookingError} />
                                </div>
                            </TextBlock>
                        </Alert>
                    )}
                    <Button
                        id="request-tickets"
                        shouldFitContainer
                        isDisabled={
                            !hasAvailability ||
                            !dateM ||
                            !time ||
                            !countTotal ||
                            isBookingInProgress ||
                            isDateBlocked
                        }
                        onClick={() => onBuyClick(false)}
                    >
                        Request Tickets
                    </Button>
                    {testRateLimiting && (
                        <Button
                            id="request-tickets-test"
                            shouldFitContainer
                            isDisabled={
                                !hasAvailability ||
                                !dateM ||
                                !time ||
                                !countTotal ||
                                isBookingInProgress ||
                                isDateBlocked
                            }
                            onClick={() => onBuyClick(true)}
                        >
                            Test Rate Limiting Messaging
                        </Button>
                    )}
                </Stack>
                <AvailabilityAlerts isFullWidth tourId={tourId} />
            </Stack>
        </Stack>
    );
}

export function BookingFormPlaceholder() {
    return (
        <FlexRow className="te-date-time-guests-picker">
            <FlexCol md="auto">
                <Placeholder height="4rem" />
            </FlexCol>
            <FlexCol md="auto">
                <Placeholder height="4rem" />
            </FlexCol>
            <FlexCol md="auto">
                <Placeholder height="4rem" />
            </FlexCol>

            <FlexCol md="auto">
                <Placeholder height="4rem" />
            </FlexCol>
        </FlexRow>
    );
}

BookingForm.propTypes = {
    isBookingInProgress: PropTypes.bool,
    onBuyClick: PropTypes.func,
    bookingError: PropTypes.string,
    counts: PropTypes.object,
    setCounts: PropTypes.func,
    time: PropTypes.string,
    setTime: PropTypes.func,
    onFocus: PropTypes.func,
    isDateBlocked: PropTypes.bool,
    openCalendar: PropTypes.bool,
    showLoginNotice: PropTypes.bool,
    showWalkupNotice: PropTypes.bool,
    showNoTicketRequiredNotice: PropTypes.bool,
    facilityId: PropTypes.string,
    guestCheckoutEnabled: PropTypes.bool,
};
